import { Box, Stack, Typography, useTheme } from "@mui/material";
import { addDays, startOfDay } from "date-fns";
import { type FC, useState } from "react";
import { type Control, useController } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { SNOOZE_OPTIONS } from "web_myplann/Reminders/Config/snoozeOptions";
import type { ReminderSnoozeFormModel } from "web_myplann/Reminders/Forms/reminderSnoozeSchema";
import { formatReminderDate } from "web_myplann/Reminders/Utils/formatReminderDate";
import { getSnoozeOptionDate } from "web_myplann/Reminders/Utils/snoozeDates";
import { ReminderDateCalendar } from "web_myplann/Shared/Components/ReminderDateCalendar";
import { ReminderPresetChip } from "web_myplann/Shared/Components/ReminderPresetChip";

type Props = {
	control: Control<ReminderSnoozeFormModel>;
	minSnoozeDate: Date;
};

export const ReminderSnoozeOptions: FC<Props> = ({
	control,
	minSnoozeDate,
}) => {
	const { t } = useResource();
	const { palette, spacingTokens, colors } = useTheme();

	const {
		field: { value, onChange },
		fieldState: { isDirty, error },
	} = useController({ control, name: "snoozedUntil" });

	const [isCustom, setIsCustom] = useState(false);

	const minCustomDate = addDays(minSnoozeDate, 1);

	const selectOption = (date: Date) => {
		setIsCustom(false);
		onChange(date);
	};

	const selectCustom = () => {
		setIsCustom(true);
		onChange(null);
	};

	return (
		<>
			<Stack direction="row" flexWrap="wrap" gap={spacingTokens.sm}>
				{SNOOZE_OPTIONS.map((option) => {
					const optionDate = getSnoozeOptionDate(minSnoozeDate, option.days);

					return (
						<ReminderPresetChip
							key={option.days}
							label={t(option.labelKey)}
							active={!isCustom && value?.getTime() === optionDate.getTime()}
							onClick={() => selectOption(optionDate)}
						/>
					);
				})}
				<ReminderPresetChip
					label={t(Resources.Reminders.Snooze.customDate)}
					active={isCustom}
					onClick={selectCustom}
				/>
			</Stack>

			{value && !isCustom && (
				<Typography
					variant="caption"
					display="block"
					color={palette.grey[500]}
					mt={spacingTokens.md}
				>
					{t(Resources.Reminders.Snooze.until, {
						date: formatReminderDate(value),
					})}
				</Typography>
			)}

			{isCustom && (
				<Box
					mx={-spacingTokens.lg}
					mt={spacingTokens.md}
					bgcolor={palette.background.paper}
					borderTop={colors.border.solid(palette.surface.border)}
				>
					<ReminderDateCalendar
						value={value}
						minDate={minCustomDate}
						onSelect={(date) => onChange(startOfDay(date))}
					/>
				</Box>
			)}

			{isDirty && error && (
				<Typography
					variant="hint"
					display="block"
					color={palette.error.main}
					mt={spacingTokens.sm}
				>
					{error.message}
				</Typography>
			)}
		</>
	);
};
